/**
 * Forms generated from field definitions. Core fields show first; the rest wait under "More details"
 * unless they already have a value. Modules can render a SchemaForm for part of a custom form.
 */
import { useId, useState } from "react";
import { UNITS, UNIT_LABELS, preferredUnit, type Field, type Fields, type Money, type Quantity } from "../../contract";
import { t } from "../lib/i18n";
import { useMe } from "../lib/session";
import { Button, Disclosure, Field as FieldShell, IconButton, Select, TextArea, TextInput, Toggle, cx } from "../ui/kit";
import { RefPicker } from "./pickers";

export type Values = Record<string, unknown>;
export type Errors = Record<string, string>;

function hasValue(v: unknown): boolean {
  if (v === undefined || v === null || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

export function SchemaForm({ fields, value, onChange, errors = {}, prefix = "", labels, showAll, only }: {
  fields: Fields; value: Values; onChange: (v: Values) => void; errors?: Errors; prefix?: string; labels?: Record<string, string>;
  /** Show every field without the "More details" fold. */
  showAll?: boolean;
  /** Render only these keys, in this order. */
  only?: string[];
}) {
  const entries = Object.entries(fields).filter(([k]) => !only || only.includes(k));
  if (only) entries.sort(([a], [b]) => only.indexOf(a) - only.indexOf(b));
  const core = entries.filter(([k, f]) => showAll || f.level !== "more" || f.required || hasValue(value[k]) || errors[`${prefix}${k}`]);
  const more = entries.filter((e) => !core.includes(e));
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const render = ([k, f]: [string, Field]) => <FieldInput key={k} name={`${prefix}${k}`} field={f} value={value[k]} onChange={(v) => set(k, v)} errors={errors} labels={labels} />;
  return (
    <div className="schema-form">
      {core.map(render)}
      {more.length > 0 && <Disclosure label={t("More details")} count={more.length}>{more.map(render)}</Disclosure>}
    </div>
  );
}

export function FieldInput({ name, field, value, onChange, errors, labels }: { name: string; field: Field; value: unknown; onChange: (v: unknown) => void; errors: Errors; labels?: Record<string, string> }) {
  const id = useId();
  const error = errors[name];
  const label = t(field.label);
  const hint = field.help ? t(field.help) : undefined;
  const optional = !field.required && field.kind !== "boolean";
  switch (field.kind) {
    case "boolean":
      return <div className="field"><Toggle checked={!!value} onChange={onChange} label={label} hint={hint} /></div>;
    case "text":
    case "tag":
      return (
        <FieldShell label={label} hint={hint} error={error} htmlFor={id} optional={optional}>
          {field.kind === "text" && field.multiline
            ? <TextArea id={id} value={(value as string) ?? ""} rows={3} placeholder={field.placeholder ? t(field.placeholder) : undefined} onChange={(e) => onChange(e.target.value)} />
            : <TextInput id={id} value={(value as string) ?? ""} className={field.kind === "tag" ? "mono" : undefined} placeholder={field.placeholder ? t(field.placeholder) : undefined} onChange={(e) => onChange(e.target.value)} />}
        </FieldShell>
      );
    case "number":
      return (
        <FieldShell label={label} hint={hint} error={error} htmlFor={id} optional={optional}>
          <div className="input-suffix">
            <TextInput id={id} type="number" inputMode="decimal" step={field.step ?? (field.integer ? 1 : "any")} min={field.min} max={field.max} value={value === undefined || value === null ? "" : String(value)}
              onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} />
            {field.suffix && <span className="suffix">{t(field.suffix)}</span>}
          </div>
        </FieldShell>
      );
    case "date":
      return (
        <FieldShell label={label} hint={hint} error={error} htmlFor={id} optional={optional}>
          <TextInput id={id} type={field.withTime ? "datetime-local" : "date"} value={typeof value === "string" ? (field.withTime ? value.slice(0, 16) : value.slice(0, 10)) : ""} onChange={(e) => onChange(e.target.value || undefined)} />
        </FieldShell>
      );
    case "enum":
      if (field.multiple) {
        const chosen = (value as string[]) ?? [];
        return (
          <FieldShell label={label} hint={hint} error={error} optional={optional}>
            <div className="choice-chips">
              {field.options.map((o) => <button key={o} type="button" className={cx("choice", chosen.includes(o) && "choice-on")} aria-pressed={chosen.includes(o)} onClick={() => onChange(chosen.includes(o) ? chosen.filter((x) => x !== o) : [...chosen, o])}>{t(field.optionLabels[o] ?? o)}</button>)}
            </div>
          </FieldShell>
        );
      }
      if (field.options.length <= 4) {
        return (
          <FieldShell label={label} hint={hint} error={error} optional={optional}>
            <div className="choice-chips" role="radiogroup" aria-label={label}>
              {field.options.map((o) => <button key={o} type="button" role="radio" aria-checked={value === o} className={cx("choice", value === o && "choice-on")} onClick={() => onChange(value === o && !field.required ? undefined : o)}>{t(field.optionLabels[o] ?? o)}</button>)}
            </div>
          </FieldShell>
        );
      }
      return (
        <FieldShell label={label} hint={hint} error={error} htmlFor={id} optional={optional}>
          <Select id={id} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value || undefined)}>
            <option value="">{t("Choose…")}</option>
            {field.options.map((o) => <option key={o} value={o}>{t(field.optionLabels[o] ?? o)}</option>)}
          </Select>
        </FieldShell>
      );
    case "quantity":
      return <FieldShell label={label} hint={hint} error={error} htmlFor={id} optional={optional}><QuantityInput id={id} field={field} value={value as Quantity | undefined} onChange={onChange} /></FieldShell>;
    case "money":
      return <FieldShell label={label} hint={hint} error={error} htmlFor={id} optional={optional}><MoneyInput id={id} value={value as Money | undefined} onChange={onChange} /></FieldShell>;
    case "ref":
      return (
        <FieldShell label={label} hint={hint} error={error} htmlFor={id} optional={optional}>
          <RefPicker id={id} target={field.target} multiple={field.multiple} value={value as string | string[] | undefined} onChange={(v) => onChange(v ?? undefined)} filter={field.filter} labels={labels} />
        </FieldShell>
      );
    case "object":
      return (
        <fieldset className="fieldset">
          <legend>{label}</legend>
          <SchemaForm fields={field.fields} value={(value as Values) ?? {}} onChange={onChange} errors={errors} prefix={`${name}.`} labels={labels} />
        </fieldset>
      );
    case "list":
      return <ListInput name={name} field={field} value={(value as unknown[]) ?? []} onChange={onChange} errors={errors} labels={labels} />;
  }
}

function QuantityInput({ id, field, value, onChange }: { id: string; field: Extract<Field, { kind: "quantity" }>; value: Quantity | undefined; onChange: (v: unknown) => void }) {
  const { data } = useMe();
  const units = field.units ?? UNITS[field.dimension];
  const fallback = preferredUnit(field.dimension, data?.ranch?.units ?? "imperial") ?? units[0] ?? "";
  const [unit, setUnit] = useState(value?.unit ?? (units.includes(fallback) ? fallback : units[0]) ?? "");
  return (
    <div className="input-group">
      <TextInput id={id} type="number" inputMode="decimal" step="any" value={value?.value ?? ""} onChange={(e) => onChange(e.target.value === "" ? undefined : { value: Number(e.target.value), unit })} />
      {units.length > 0 ? (
        <Select aria-label={t("Unit")} value={value?.unit ?? unit} onChange={(e) => { setUnit(e.target.value); if (value) onChange({ ...value, unit: e.target.value }); }}>
          {units.map((u) => <option key={u} value={u}>{t(UNIT_LABELS[u] ?? u)}</option>)}
        </Select>
      ) : <TextInput aria-label={t("Unit")} value={value?.unit ?? unit} onChange={(e) => { setUnit(e.target.value); if (value) onChange({ ...value, unit: e.target.value }); }} />}
    </div>
  );
}

function MoneyInput({ id, value, onChange }: { id: string; value: Money | undefined; onChange: (v: unknown) => void }) {
  const { data } = useMe();
  const currency = value?.currency ?? data?.ranch?.currency ?? "USD";
  const [text, setText] = useState(value ? (value.minor / 100).toFixed(2) : "");
  return (
    <div className="input-prefix">
      <span className="prefix">{currency === "USD" ? "$" : currency}</span>
      <TextInput id={id} inputMode="decimal" value={text} onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value.replace(/[,$\s]/g, ""));
        onChange(e.target.value.trim() === "" || Number.isNaN(n) ? undefined : { minor: Math.round(n * 100), currency });
      }} />
    </div>
  );
}

function ListInput({ name, field, value, onChange, errors, labels }: { name: string; field: Extract<Field, { kind: "list" }>; value: unknown[]; onChange: (v: unknown) => void; errors: Errors; labels?: Record<string, string> }) {
  const itemLabel = t(field.itemLabel ?? field.of.label);
  return (
    <fieldset className="fieldset list-input">
      <legend>{t(field.label)}</legend>
      {value.map((item, i) => (
        <div key={i} className="list-item">
          <div className="list-item-head"><span>{itemLabel} {i + 1}</span><IconButton icon="delete" label={t("Remove")} onClick={() => onChange(value.filter((_, j) => j !== i))} /></div>
          {field.of.kind === "object"
            ? <SchemaForm fields={field.of.fields} value={(item as Values) ?? {}} onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} errors={errors} prefix={`${name}.${i}.`} labels={labels} />
            : <FieldInput name={`${name}.${i}`} field={{ ...field.of, label: itemLabel }} value={item} onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} errors={errors} labels={labels} />}
        </div>
      ))}
      <Button size="sm" icon="plus" onClick={() => onChange([...value, field.of.kind === "object" ? {} : undefined])}>{t("Add {{item}}", { item: itemLabel.toLowerCase() })}</Button>
    </fieldset>
  );
}

/** Map API issues ("data.calves.0.sex") to form error keys. */
export function issuesToErrors(issues: Array<{ path: string; message: string }>, strip = "data."): Errors {
  const out: Errors = {};
  for (const i of issues) out[i.path.startsWith(strip) ? i.path.slice(strip.length) : i.path] = t(i.message);
  return out;
}
